import type { Ref } from "react";
import { useEffect, useRef } from "react";

import isNil from "lodash/isNil";

import type { PdfProps, PdfRef } from "@/crossPlatform/Pdf/types";
import { openBrowserTab } from "@/utils/navigation.util";

const NoopProps = {
  numberOfPages: 0,
  path: "",
  size: { height: 0, width: 0 },
} as const;

// web ignores the ref (native's react-native-pdf uses it to call methods like setPage);
//  here we just open a tab, so it's unused — but React 19 lets us accept `ref` as a plain
//  prop so native callers passing `ref` still typecheck, without needing forwardRef
const Pdf = ({
  onError,
  onLoadComplete,
  ref: _ref,
  source,
}: { ref?: Ref<PdfRef> } & PdfProps) => {
  // refs to keep useEffect exhaustive dependencies (best practice) but not re-fire only if prop callbacks changes
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  const onLoadCompleteRef = useRef(onLoadComplete);
  useEffect(() => {
    onLoadCompleteRef.current = onLoadComplete;
  }, [onLoadComplete]);

  // on web, open a new tab on mount once have a valid URI
  //  (a web "WebView" would be an iframe, which would require updating my.equip.health's
  //   iframe headers just for its PDF assets, which is too heavy handed; a tab is opened instead)
  const uri = typeof source === "number" ? undefined : source.uri;
  useEffect(() => {
    if (isNil(uri)) {
      onErrorRef.current?.(new Error("No URI provided for PDF"));
      return;
    }

    const newWindow = openBrowserTab(uri);
    // since web opens a tab and not a react-pdf, these props are noop'd, but callback invoked to indicate "done"
    onLoadCompleteRef.current?.(
      NoopProps.numberOfPages,
      NoopProps.path,
      NoopProps.size,
    );
    if (isNil(newWindow)) {
      onErrorRef.current?.(new Error("Failed to open PDF"));
    }
  }, [uri]);

  return null;
};

export { Pdf };
